import React from "react";

import {
  getCompactConfiguration,
  isSearchFormCompact
} from "@app/components/search-form/helpers";
import type {
  SearchFormStyleVariant,
  SearchFormVariant
} from "@app/components/search-form/search-form";
import { useIsSearchFormExpanded } from "@app/components/search-form/search-form-client-state";
import { useSearchLocationValues } from "@app/components/search-form-hydrated/autocomplete-input-hydrated/hooks/use-search-state-hooks";
import { useIsMatchingPage } from "@app/context/use-entity";
import { useLiteAppContext } from "@app/helpers/hooks";

const SEARCH_FORM_ID = "search-form";

interface SearchFormState {
  variant: SearchFormVariant | undefined;
  style_variant?: SearchFormStyleVariant;
  one_way_only: boolean;
  profile_ids?: number[];
  is_compact: boolean;
  compact_configuration: ReturnType<typeof getCompactConfiguration>;
}

interface UseSearchFormStateProps {
  variant?: SearchFormVariant;
  style_variant?: SearchFormStyleVariant;
  one_way_only?: boolean;
  profile_ids?: number[];
}

const useIsCompactSearchXpEnabled = (): boolean => {
  const is_xp_enabled = isSearchFormCompact();
  const is_page_enabled = useIsMatchingPage(["route", "train-route"]);
  const { origin, destination } = useSearchLocationValues();
  const has_origin_or_destination = !!origin || !!destination;

  return is_xp_enabled && is_page_enabled && has_origin_or_destination;
};

export const useSearchFormState = (
  props: UseSearchFormStateProps
): SearchFormState => {
  const { device } = useLiteAppContext();
  const is_compact_xp_enabled = useIsCompactSearchXpEnabled();
  const is_server = typeof window === "undefined";
  const is_mobile = !!device?.is_mobile;

  const client_state = React.useMemo(() => {
    if (is_server || props.variant) {
      return null;
    }

    const search_form = document.getElementById(SEARCH_FORM_ID);
    if (!search_form) {
      throw new Error("SearchForm was not rendered on the server");
    }

    const data_variant = search_form.getAttribute("data-variant");
    if (!data_variant) {
      throw new Error(
        "SearchForm was not rendered with variant data attribute"
      );
    }

    const profile_ids = search_form.getAttribute("data-profile-ids");
    const data_style_variant =
      search_form.getAttribute("data-style-variant") || undefined;
    const data_one_way_only = search_form.getAttribute("data-one-way-only");
    const data_is_compact = search_form.getAttribute("data-is-compact");

    return {
      variant: data_variant as SearchFormVariant,
      style_variant: data_style_variant as SearchFormStyleVariant | undefined,
      one_way_only: data_one_way_only === "true",
      is_compact: data_is_compact === "true",
      profile_ids: profile_ids
        ? (JSON.parse(profile_ids) as number[])
        : undefined
    };
  }, [is_server, props.variant]);

  if (client_state) {
    // Client-side logic
    const compact_configuration = getCompactConfiguration(client_state.variant);

    return {
      ...client_state,
      compact_configuration
    };
  }

  // Server-side logic OR when variant is provided (like in tests)
  const is_compact: boolean = is_compact_xp_enabled && is_mobile;

  const compact_configuration = getCompactConfiguration(props.variant);

  return {
    variant: props.variant,
    style_variant: props.style_variant,
    one_way_only: props.one_way_only || false,
    profile_ids: props.profile_ids,
    is_compact,
    compact_configuration
  };
};

export const useSearchFormLabels = (
  variant: SearchFormVariant | undefined
): string => {
  const { liteTranslator } = useLiteAppContext();

  const search_labels: Record<SearchFormVariant, string> = React.useMemo(
    () => ({
      "landing-page": liteTranslator.t("!landing.input-label.search"),
      results: liteTranslator.t("!results.search.submit"),
      "results-menu": liteTranslator.t("!results.search.submit")
    }),
    [liteTranslator]
  );

  return (
    search_labels[variant as SearchFormVariant] || search_labels["landing-page"]
  );
};

export const useIsCompactExpanded = (): boolean => {
  const is_server = typeof window === "undefined";
  const client_expanded = useIsSearchFormExpanded();

  return is_server ? false : client_expanded;
};
